(function () {
  'use strict';

  const PRODUCTS = (window.__PC && window.__PC.products) || [];
  const ROUTES_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ── Sort: make A→Z, then model A→Z (numeric-aware: 2 Series < 10 Series, F150 < F250) ── */
  const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
  PRODUCTS.sort((a, b) => collator.compare(a.make, b.make) || collator.compare(a.model, b.model));

  /* ── Build make→models tree (shared by all instances) ────────── */
  const makeTree = {};
  PRODUCTS.forEach(p => {
    if (!makeTree[p.make]) makeTree[p.make] = [];
    makeTree[p.make].push(p);
  });
  const allMakes = Object.keys(makeTree).sort(collator.compare);

  /* Priority by collection handle — independent of how make names are written */
  const priorityMakeHandles = ['chevrolet','dodge','ford','gmc','jeep','ram','toyota'];
  const makeHandleOf = {};
  PRODUCTS.forEach(p => { makeHandleOf[p.make] = p.makeHandle; });
  const isPriority = m => priorityMakeHandles.includes(makeHandleOf[m]);
  const bestMakes  = allMakes.filter(isPriority);
  const otherMakes = allMakes.filter(m => !isPriority(m));

  function productUrl(handle, variantId) {
    const base = `${ROUTES_ROOT}products/${encodeURIComponent(handle)}`;
    return variantId ? `${base}?variant=${variantId}` : base;
  }

  function starsHtml(rating) {
    const val = parseFloat(rating);
    if (!rating || isNaN(val)) return '';
    const full = Math.round(val);
    return '★'.repeat(full) + '☆'.repeat(Math.max(0, 5 - full));
  }

  /* ── One selector instance ───────────────────────────────────── */
  function initVD(root) {
    let CFG = {};
    try {
      CFG = JSON.parse(root.querySelector('.pc-vd__config').textContent);
    } catch (_) { /* fall through to defaults */ }
    const ratings      = CFG.ratings || {};
    const splitterBest = CFG.splitterBest || 'BEST SELLERS';
    const splitterAll  = CFG.splitterAll  || 'ALL MAKES';
    const ratedHandles = new Set(Object.keys(ratings));

    /* ── DOM refs ─────────────────────────────────────────────── */
    const $ = sel => root.querySelector(sel);
    const el = {
      trigger:         $('.pc-vd__trigger'),
      panel:           $('.pc-vd__panel'),
      nav:             $('.pc-vd__nav'),
      search:          $('.pc-vd__search'),
      searchWrap:      $('.pc-vd__search-wrap'),
      searchClear:     $('.pc-vd__search-clear'),
      list:            $('.pc-vd__list'),
      loading:         $('.pc-vd__loading'),
      empty:           $('.pc-vd__empty'),
      emptyQuery:      $('.pc-vd__empty-query'),
      redirect:        $('.pc-vd__redirect'),
    };

    /* ── State ────────────────────────────────────────────────── */
    let activeMake    = null;
    let isOpen        = false;
    let isRedirecting = false;

    /* ── UI helpers ───────────────────────────────────────────── */
    function showLoading(on) {
      el.loading.classList.toggle('is-visible', on);
      el.list.style.display = on ? 'none' : '';
    }

    function showEmpty(on, query) {
      el.empty.classList.toggle('is-visible', on);
      if (on && query !== undefined) el.emptyQuery.textContent = query;
    }

    function highlight(item, scroll) {
      el.list.querySelectorAll('.pc-vd__item.is-highlighted')
        .forEach(i => i.classList.remove('is-highlighted'));
      item.classList.add('is-highlighted');
      if (scroll) item.scrollIntoView({ block: 'nearest' });
    }

    /* Search scope per level: makes → all, make → its models, variants → hidden */
    function setSearchScope(level) {
      el.search.value = '';
      el.searchClear.hidden = true;
      el.searchWrap.hidden = level === 'variants';
      el.search.placeholder = level === 'models'
        ? `Search ${activeMake} models…`
        : 'Search make or model…';
    }

    function highlightFirst() {
      const first = el.list.querySelector('.pc-vd__item');
      if (first) highlight(first);
    }

    /* ── Render helpers ───────────────────────────────────────── */
    /* Product/make/variant names come from store data — always set as text, never HTML */
    function nameSpan(text) {
      const span = document.createElement('span');
      span.className = 'pc-vd__item-name';
      span.textContent = text;
      return span;
    }

    function renderSep(text) {
      const li = document.createElement('li');
      li.className = 'pc-vd__sep';
      li.setAttribute('role', 'presentation');
      li.textContent = text;
      el.list.appendChild(li);
    }

    function renderMakeItem(make) {
      const li = document.createElement('li');
      li.className = 'pc-vd__item';
      li.setAttribute('role', 'option');
      li.dataset.make = make;
      li.appendChild(nameSpan(make));
      li.insertAdjacentHTML('beforeend', `
        <svg class="pc-vd__item-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M4.5 2.5L9.5 7L4.5 11.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`);
      li.addEventListener('click', e => { e.stopPropagation(); drillIntoMake(make); });
      li.addEventListener('mouseenter', () => highlight(li));
      el.list.appendChild(li);
    }

    function renderModelItem(product, skipMakeName) {
      const li = document.createElement('li');
      li.className = 'pc-vd__item';
      li.setAttribute('role', 'option');
      li.dataset.handle = product.handle;

      const label = skipMakeName ? product.model : `${product.make} ${product.model}`;
      const rt = ratings[product.handle];
      const stars = rt ? starsHtml(rt.rating) : '';
      const ratingHtml = stars ? `
        <span class="pc-vd__item-rating">
          <span class="pc-vd__item-stars" aria-hidden="true">${stars}</span>
          ${rt.raters && parseInt(rt.raters) > 0
            ? `<span class="pc-vd__item-raters">(${parseInt(rt.raters).toLocaleString()})</span>`
            : ''}
        </span>` : '';

      /* ratingHtml is safe markup: only ★/☆ and parseInt output */
      li.appendChild(nameSpan(label));
      if (ratingHtml) li.insertAdjacentHTML('beforeend', ratingHtml);
      li.addEventListener('click', e => { e.stopPropagation(); selectModel(product, li); });
      li.addEventListener('mouseenter', () => highlight(li));
      el.list.appendChild(li);
    }

    function renderVariantItems(variants, product) {
      /* Count how many variants share the same body/year */
      const bodyYearCount = {};
      variants.forEach(v => {
        const by = v.title.split(' / ')[3] || v.title;
        bodyYearCount[by] = (bodyYearCount[by] || 0) + 1;
      });

      variants.forEach(variant => {
        const parts    = variant.title.split(' / ');
        const bodyYear = parts[3] || variant.title;
        const engine   = parts[4] ? parts[4].replace(/^\(|\)$/g, '') : '';
        const label    = (bodyYearCount[bodyYear] > 1 && engine)
          ? `${bodyYear} · ${engine}`
          : bodyYear;

        const li = document.createElement('li');
        li.className = 'pc-vd__item pc-vd__item--variant';
        li.setAttribute('role', 'option');
        li.appendChild(nameSpan(label));
        li.addEventListener('click', e => {
          e.stopPropagation();
          navigateTo(productUrl(product.handle, variant.id), label);
        });
        li.addEventListener('mouseenter', () => highlight(li));
        el.list.appendChild(li);
      });
    }

    /* ── Views ────────────────────────────────────────────────── */
    function renderMakes() {
      el.list.innerHTML = '';
      showEmpty(false);
      showLoading(false);
      activeMake = null;
      updateNav();
      setSearchScope('makes');

      if (bestMakes.length) {
        renderSep(splitterBest);
        bestMakes.forEach(m => renderMakeItem(m));
      }
      if (otherMakes.length) {
        renderSep(splitterAll);
        otherMakes.forEach(m => renderMakeItem(m));
      }
      highlightFirst();
    }

    function drillIntoMake(make) {
      activeMake = make;
      el.list.innerHTML = '';
      showEmpty(false);
      setSearchScope('models');
      updateNav();

      const products = makeTree[make] || [];
      const best  = products.filter(p => ratedHandles.has(p.handle));
      const other = products.filter(p => !ratedHandles.has(p.handle));

      if (best.length) {
        renderSep(splitterBest);
        best.forEach(p => renderModelItem(p, true));
      }
      if (other.length) {
        renderSep('MODELS');
        other.forEach(p => renderModelItem(p, true));
      }
      el.list.scrollTop = 0;
      highlightFirst();
    }

    async function selectModel(product, triggerEl) {
      if (isRedirecting) return;

      /* Show loading spinner during fetch */
      showLoading(true);
      if (triggerEl) triggerEl.style.pointerEvents = 'none';

      let variants;
      try {
        const res = await fetch(`${productUrl(product.handle)}.js`, { cache: 'force-cache' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        variants   = data.variants || [];
      } catch (_) {
        /* Fetch failed — fall back to the plain product page */
        variants = [];
      }

      showLoading(false);
      if (triggerEl) triggerEl.style.pointerEvents = '';

      if (!variants.length) {
        navigateTo(productUrl(product.handle), product.model);
        return;
      }

      if (variants.length === 1) {
        navigateTo(productUrl(product.handle, variants[0].id), product.model);
        return;
      }

      /* Model may come from global search (activeMake null) — sync it so
         breadcrumb and back button point to the product's make */
      activeMake = product.make;
      showEmpty(false);
      setSearchScope('variants');
      updateNav(product);
      el.list.innerHTML = '';
      renderSep(`${product.make} ${product.model}`);
      renderVariantItems(variants, product);
      el.list.scrollTop = 0;
      highlightFirst();
      /* Search is hidden — keep keyboard nav alive by focusing the list */
      el.list.focus({ preventScroll: true });
    }

    /* ── Navigation / Breadcrumb ──────────────────────────────── */
    function updateNav(activeProduct) {
      el.nav.innerHTML = '';
      if (!activeMake && !activeProduct) return;

      /* Back arrow — round button */
      const backBtn = document.createElement('button');
      backBtn.type = 'button';
      backBtn.className = 'pc-vd__nav-back';
      backBtn.innerHTML = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M7.5 2L3.5 6L7.5 10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;

      /* Breadcrumb trail */
      const crumbs = document.createElement('div');
      crumbs.className = 'pc-vd__nav-crumbs';

      if (activeProduct) {
        /* Level 3 — variants */
        backBtn.setAttribute('aria-label', `Back to ${activeMake}`);
        backBtn.addEventListener('click', e => { e.stopPropagation(); drillIntoMake(activeMake); });

        const c1 = document.createElement('span');
        c1.className = 'pc-vd__nav-crumb';
        c1.textContent = 'All Makes';
        c1.addEventListener('click', e => { e.stopPropagation(); renderMakes(); });

        const s1 = document.createElement('span');
        s1.className = 'pc-vd__nav-sep';
        s1.textContent = '›';

        const c2 = document.createElement('span');
        c2.className = 'pc-vd__nav-crumb';
        c2.textContent = activeMake;
        c2.addEventListener('click', e => { e.stopPropagation(); drillIntoMake(activeMake); });

        const s2 = document.createElement('span');
        s2.className = 'pc-vd__nav-sep';
        s2.textContent = '›';

        const c3 = document.createElement('span');
        c3.className = 'pc-vd__nav-crumb is-active';
        c3.textContent = activeProduct.model;

        crumbs.append(c1, s1, c2, s2, c3);
      } else {
        /* Level 2 — models */
        backBtn.setAttribute('aria-label', 'Back to all makes');
        backBtn.addEventListener('click', e => { e.stopPropagation(); renderMakes(); });

        const c2 = document.createElement('span');
        c2.className = 'pc-vd__nav-crumb is-active';
        c2.textContent = activeMake;

        crumbs.append(c2);
      }

      el.nav.append(backBtn, crumbs);
    }

    /* ── Search ───────────────────────────────────────────────── */
    el.search.addEventListener('input', () => {
      const q = el.search.value.trim().toLowerCase();
      el.searchClear.hidden = !q;

      if (!q) {
        activeMake ? drillIntoMake(activeMake) : renderMakes();
        return;
      }

      el.list.innerHTML = '';
      showEmpty(false);

      /* Level 2 — only the active make's models; Level 1 — all makes + models */
      const inMake = !!activeMake;
      const matchMakes = inMake ? [] : allMakes.filter(m => m.toLowerCase().includes(q));
      const matchProducts = (inMake ? makeTree[activeMake] || [] : PRODUCTS).filter(p =>
        p.model.toLowerCase().includes(q) || `${p.make} ${p.model}`.toLowerCase().includes(q)
      );

      if (!matchMakes.length && !matchProducts.length) {
        showEmpty(true, el.search.value.trim());
        return;
      }

      if (matchMakes.length) {
        renderSep('MAKES');
        matchMakes.forEach(m => renderMakeItem(m));
      }
      if (matchProducts.length) {
        renderSep('MODELS');
        matchProducts.forEach(p => renderModelItem(p, inMake));
      }
      highlightFirst();
    });

    el.searchClear.addEventListener('click', () => {
      el.search.value = '';
      el.searchClear.hidden = true;
      activeMake ? drillIntoMake(activeMake) : renderMakes();
      el.search.focus();
    });

    /* ── Keyboard navigation ──────────────────────────────────── */
    /* Listens on the panel so it works from the search input and from the
       list (focused on the variants step, where search is hidden) */
    el.list.tabIndex = -1;
    el.panel.addEventListener('keydown', e => {
      if (e.target !== el.search && e.target !== el.list) return;

      const items = Array.from(el.list.querySelectorAll('.pc-vd__item'));
      const cur   = el.list.querySelector('.pc-vd__item.is-highlighted');
      const idx   = items.indexOf(cur);
      if (!items.length && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          highlight(items[idx < items.length - 1 ? idx + 1 : 0], true);
          break;
        case 'ArrowUp':
          e.preventDefault();
          highlight(items[idx > 0 ? idx - 1 : items.length - 1], true);
          break;
        case 'Enter':
          e.preventDefault();
          if (cur) cur.click();
          break;
        case 'Escape':
          closePanel();
          el.trigger.focus();
          break;
      }
    });

    /* ── Navigate to product ──────────────────────────────────── */
    function navigateTo(url, crumbLabel) {
      if (isRedirecting) return;
      isRedirecting = true;

      /* Add selection as last crumb in breadcrumb */
      if (crumbLabel) {
        const crumbs = el.nav.querySelector('.pc-vd__nav-crumbs');
        if (crumbs) {
          const sep = document.createElement('span');
          sep.className = 'pc-vd__nav-sep';
          sep.textContent = '›';

          const crumb = document.createElement('span');
          crumb.className = 'pc-vd__nav-crumb is-active';
          crumb.textContent = crumbLabel;

          crumbs.append(sep, crumb);
        }
      }

      el.redirect.classList.add('is-visible');
      el.list.style.pointerEvents = 'none';
      el.search.disabled = true;
      window.location.href = url;
    }

    /* ── Panel open / close ───────────────────────────────────── */
    function openPanel() {
      isOpen = true;
      el.panel.classList.add('is-open');
      el.trigger.setAttribute('aria-expanded', 'true');
      renderMakes();
      setTimeout(() => el.search.focus(), 50);
    }

    function closePanel() {
      isOpen = false;
      el.panel.classList.remove('is-open');
      el.trigger.setAttribute('aria-expanded', 'false');
      isRedirecting = false;
      showLoading(false);
      el.list.style.pointerEvents = '';
      el.search.disabled = false;
      el.redirect.classList.remove('is-visible');
    }

    /* No stopPropagation here — the document handler below lets the click
       close any other open instance, while this root ignores it */
    el.trigger.addEventListener('click', () => {
      isOpen ? closePanel() : openPanel();
    });

    document.addEventListener('click', e => {
      if (isOpen && !root.contains(e.target)) closePanel();
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && isOpen) { closePanel(); el.trigger.focus(); }
    });

    /* Back button restores the page from bfcache with the redirect overlay
       still up and isRedirecting stuck at true — reset to a clean state */
    window.addEventListener('pageshow', e => {
      if (e.persisted) closePanel();
    });
  }

  /* Script tag is emitted once per snippet render — init each root only once */
  document.querySelectorAll('.pc-vd').forEach(root => {
    if (root.dataset.pcVdReady) return;
    root.dataset.pcVdReady = '1';
    initVD(root);
  });

})();
